import { createContext, useContext, useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore, type ReactNode } from "react";
import { useConvex } from "convex/react";
import { enhanceArticle, inLibrary, isSponsored, mutedBy, pauseOtherMedia, playerCommand, spokenBlocks } from "@vladrss/core";
import { api } from "../lib/api";
import { useArticleActions, useFeeds, useSettings } from "../lib/library";
import { Listener, type SpokenArticle } from "../lib/listener";
import { BrowserSpeaker, listenRate, setListenRate, setSpeechKey, setSpeechVoice, speechVoices, voiceSettings } from "../lib/speaker";
import { getToken } from "../lib/session";
import { forgetArticles, getState, remember, rowCache, subscribe, toast, useStore, withOverlay } from "../lib/store";
import { sanitize } from "../lib/sanitize";
import { Icon } from "./Icon";

interface ListenContext { listener: Listener; start(): void; reveal(): void; }
const Context = createContext<ListenContext | null>(null);
export function useListen() {
  const context = useContext(Context);
  if (!context) throw new Error("Listen requires its provider");
  const state = useSyncExternalStore(context.listener.subscribe, () => context.listener.state);
  return { ...context, state };
}

const visibleArticle = () => document.querySelector<HTMLElement>("article[data-article-id]");
const spokenText = new WeakMap<Element, string>();
export function markSpeech(root: ParentNode) {
  const blocks = spokenBlocks(root);
  blocks.forEach(({ element, text }, n) => { (element as HTMLElement).dataset.say = String(n); spokenText.set(element, text); });
  dispatchEvent(new Event("vladrss:speech-ready"));
}
function silenceMedia() {
  pauseOtherMedia(document);
  for (const audio of document.querySelectorAll("audio")) audio.pause();
  for (const frame of document.querySelectorAll("iframe")) if (/youtube(?:-nocookie)?\.com\/embed\//.test(frame.src)) playerCommand(frame, "pauseVideo");
}

/** One queue per signed-in tab, with one-shot body reads only when an article becomes spoken. */
export function ListenProvider({ children, open }: { children: ReactNode; open(id: string): void }) {
  const client = useConvex(), feeds = useFeeds(), settings = useSettings(), { read } = useArticleActions();
  const latest = useRef({ feeds, settings, read, open }); latest.current = { feeds, settings, read, open };
  const following = useRef(true), revealPending = useRef(false);
  const eligible = (id: string) => {
    if (getState().removed.has(id)) return false;
    const raw = rowCache.get(id); if (!raw) return true;
    const row = withOverlay(raw), { feeds, settings } = latest.current;
    return row.kind === "article" && inLibrary(row.isSaved, feeds !== undefined && !feeds.some(f => f.id === row.feedId && !f.deleted), row.retiredAt !== undefined)
      && !mutedBy(row, settings?.mutes ?? []) && !isSponsored(row);
  };
  const [listener] = useState(() => new Listener({
    speaker: new BrowserSpeaker(toast), eligible,
    unread: id => { const r = rowCache.get(id); return !r || !withOverlay(r).isRead; },
    finished: id => { latest.current.read(id, true); },
    article: (item, previous) => { if (getState().reading?.id === previous && previous !== item.id) latest.current.open(item.id); },
    error: toast,
    load: async (id, signal) => {
      const token = getToken() ?? "";
      const row = await client.query(api.articles.get, { id: id as never, token }); signal.throwIfAborted();
      if (!row) { forgetArticles([id]); return null; }
      remember([row]); if (!eligible(id)) return null;
      if (row.body !== "full") { await client.action(api.content.ensure, { id: id as never, token }); signal.throwIfAborted(); }
      const body = await client.query(api.content.body, { id: id as never, token }); signal.throwIfAborted();
      // A template is inert: preparing spoken text must not download images or run embeds.
      const template = document.createElement("template"); template.innerHTML = sanitize(body?.html ?? "", row.author);
      enhanceArticle(template.content);
      const paragraphs = spokenBlocks(template.content).map(p => p.text);
      if (!paragraphs.length) return null;
      return { id, title: row.title, source: latest.current.feeds?.find(f => f.id === row.feedId)?.title ?? "your sources", paragraphs };
    },
  }));
  const state = useSyncExternalStore(listener.subscribe, () => listener.state);
  const reading = useStore(s => s.reading);
  const paint = () => {
    document.querySelectorAll("[data-speaking]").forEach(el => el.removeAttribute("data-speaking"));
    if (!listener.state.active) return;
    const article = visibleArticle(); if (article?.dataset.articleId !== listener.state.id) return;
    const block = article.querySelector<HTMLElement>(`[data-say="${listener.state.block}"]`);
    if (!block || spokenText.get(block) !== listener.blockText(listener.state.block)) return;
    block.dataset.speaking = "";
    if (following.current || revealPending.current) { block.scrollIntoView({ block: "nearest", behavior: "instant" }); revealPending.current = false; }
  };
  const reveal = () => { following.current = true; revealPending.current = true; latest.current.open(listener.state.id); requestAnimationFrame(paint); };
  const start = () => {
    const reading = getState().reading, article = visibleArticle(); if (!reading) return;
    const row = rowCache.get(reading.id);
    if (!row || !eligible(reading.id)) { toast("Listen is available for articles with text"); return; }
    const blocks = article ? spokenBlocks(article.querySelector(".prose") ?? document.createDocumentFragment()) : [];
    const scroller = article?.closest<HTMLElement>("[data-reader]");
    const block = scroller && scroller.scrollTop > 60 ? blocks.findIndex(p => p.element.getBoundingClientRect().bottom > scroller.getBoundingClientRect().top + 60) : -1;
    const first: SpokenArticle | undefined = row.body === "full" && blocks.length ? { id: row.id, title: row.title, source: latest.current.feeds?.find(f => f.id === row.feedId)?.title ?? "your sources", paragraphs: blocks.map(p => p.text) } : undefined;
    following.current = true; silenceMedia();
    listener.start(reading.session.slice(Math.max(0, reading.session.indexOf(reading.id))), block, first);
  };
  useLayoutEffect(paint, [state, reading?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const changed = () => listener.invalidate();
    const off = subscribe(changed);
    addEventListener("vladrss:speech-ready", paint);
    const manual = (e: Event) => { if ((e.target as Element)?.closest?.("[data-reader]")) following.current = false; };
    const keyboard = (e: KeyboardEvent) => { if ([" ", "PageDown", "PageUp", "Home", "End", "ArrowDown", "ArrowUp"].includes(e.key)) manual(e); };
    const seek = (e: MouseEvent) => {
      if (!listener.state.active || !getSelection()?.isCollapsed || (e.target as Element).closest("a, button, input, figure, .cmp")) return;
      const el = (e.target as Element).closest<HTMLElement>("[data-say]");
      if (el?.closest<HTMLElement>("[data-article-id]")?.dataset.articleId !== listener.state.id) return;
      const block = Number(el!.dataset.say);
      if (spokenText.get(el!) !== listener.blockText(block)) return; // a newer body must never seek the old queue's wrong paragraph
      following.current = true; listener.seek(block);
    };
    const pause = () => { if (listener.state.playing) listener.pause(); };
    const nativeMedia = (e: Event) => { if ((e.target as Element).matches("video, audio")) pause(); };
    document.addEventListener("wheel", manual, { passive: true }); document.addEventListener("touchstart", manual, { passive: true });
    document.addEventListener("keydown", keyboard); document.addEventListener("click", seek); document.addEventListener("play", nativeMedia, true);
    addEventListener("vladrss:media-play", pause);
    return () => {
      off(); listener.stop(); if (!getToken()) setSpeechKey(""); removeEventListener("vladrss:speech-ready", paint); removeEventListener("vladrss:media-play", pause);
      document.removeEventListener("wheel", manual); document.removeEventListener("touchstart", manual); document.removeEventListener("keydown", keyboard); document.removeEventListener("click", seek); document.removeEventListener("play", nativeMedia, true);
      document.querySelectorAll("[data-speaking]").forEach(el => el.removeAttribute("data-speaking"));
    };
  }, [listener]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { listener.invalidate(); }, [listener, feeds, settings]);
  useEffect(() => { listener.rate(listenRate()); }, [listener]);
  useEffect(() => { if (state.playing) silenceMedia(); }, [state.playing]);
  useEffect(() => {
    if (!("mediaSession" in navigator) || !state.active) return;
    const actions: [MediaSessionAction, () => void][] = [["play", () => listener.resume()], ["pause", () => listener.pause()], ["previoustrack", () => listener.previous()], ["nexttrack", () => listener.next()], ["stop", () => listener.stop()]];
    for (const [action, fn] of actions) { try { navigator.mediaSession.setActionHandler(action, fn); } catch { /* unsupported in this browser */ } }
    return () => { for (const [action] of actions) { try { navigator.mediaSession.setActionHandler(action, null); } catch { /* unsupported */ } } navigator.mediaSession.metadata = null; navigator.mediaSession.playbackState = "none"; };
  }, [listener, state.active]);
  useEffect(() => {
    if (!("mediaSession" in navigator) || !state.active) return;
    if ("MediaMetadata" in window) navigator.mediaSession.metadata = new MediaMetadata({ title: state.title, artist: state.source, album: "VladRSS Listen" });
    navigator.mediaSession.playbackState = state.playing ? "playing" : "paused";
  }, [state.active, state.title, state.source, state.playing]);
  return <Context.Provider value={{ listener, start, reveal }}>{children}</Context.Provider>;
}

export function ListenBar() {
  const { listener, state, reveal } = useListen();
  if (!state.active) return null;
  return <div className="upnext listenbar" role="region" aria-label="Listen player">
    <button className="up-play" aria-label={state.playing ? "Pause Listen" : "Resume Listen"} onClick={() => listener.toggle()}><Icon name={state.playing ? "pause" : "play"} /></button>
    <button className="up-t" onClick={reveal} aria-label="Return to spoken article"><span className="up-l">{state.loading ? "Preparing…" : state.playing ? state.voice : "Paused"}</span><span className="up-h">{state.title || "Loading article…"}</span></button>
    <div className="listen-controls">
      <button className="ib lc-extra" aria-label="Listen: back one paragraph" data-tip="Back one paragraph" onClick={() => listener.previous()}><Icon name="replay" /></button>
      <button className="btn ghost" aria-label={`Listen speed ${state.rate} times`} onClick={() => { const n = state.rate >= 1.2 ? 1 : Math.round((state.rate + .1) * 10) / 10; setListenRate(n); listener.rate(n); }}>{state.rate}×</button>
      <button className="ib lc-extra" aria-label="Listen: skip to the next article" data-tip="Skip to the next article" onClick={() => listener.next()}><Icon name="skipNext" /></button>
      <button className="ib" aria-label="Stop Listen" onClick={() => listener.stop()}><Icon name="x" /></button>
    </div>
  </div>;
}

export function ListenSettings() {
  const { listener } = useListen();
  const [config, setConfig] = useState(voiceSettings), [draft, setDraft] = useState("");
  const [voices, setVoices] = useState<{ id: string; name: string }[]>([]), [status, setStatus] = useState("");
  useEffect(() => {
    if (!config.hasKey) return;
    const controller = new AbortController(); setStatus("Loading voices…");
    void speechVoices(controller.signal).then(v => {
      if (controller.signal.aborted) return;
      setVoices(v); setStatus(v.length ? "" : "No English voices available; the browser voice will be used");
      if (v.length && !voiceSettings().voice) { setSpeechVoice(v[0]!.id); setConfig(voiceSettings()); }
    }, e => { if (!controller.signal.aborted) setStatus((e as Error).message); });
    return () => controller.abort();
  }, [config.hasKey, config.keyVersion]);
  return <section className="listen-settings" aria-label="Listen settings"><h3>Listen</h3>
    <p className="muted">Speechify reads with a natural voice. If unavailable, Listen uses your browser’s voice. Your key stays in this tab until you close it or sign out; it is sent only to Speechify.</p>
    <form onSubmit={e => { e.preventDefault(); listener.stop(); setSpeechKey(draft); setDraft(""); setVoices([]); setConfig(voiceSettings()); }}>
      <label className="lab" htmlFor="speech-key">Speechify API key {config.hasKey && <small>· entered for this tab</small>}</label>
      <div className="listen-key"><input className="inp" id="speech-key" type="password" autoComplete="off" spellCheck={false} value={draft} onChange={e => setDraft(e.target.value)} placeholder={config.hasKey ? "Replace key" : "Optional"} /><button className="btn" disabled={!draft.trim()}>Use key</button>{config.hasKey && <button className="btn" type="button" onClick={() => { listener.stop(); setSpeechKey(""); setConfig(voiceSettings()); setVoices([]); setStatus(""); }}>Forget key</button>}</div>
    </form>
    {config.hasKey && voices.length > 0 && <label className="lab">Speechify voice<select className="sel" aria-label="Speechify voice" value={config.voice || voices[0]!.id} onChange={e => { listener.stop(); setSpeechVoice(e.target.value); setConfig(voiceSettings()); }}>{voices.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}</select></label>}
    {status && <p role="status">{status}</p>}
  </section>;
}
